<script setup lang="ts">
import { helpers } from '@vuelidate/validators'

import { Beneficiary, type Collection, Form } from '~/layers/forms/models'
import { minLengthValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Collection<Beneficiary>
  requireBeneficiaries?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  requireBeneficiaries: false,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const { beneficiariesCountIsMaxed, constraints } = useContractConstraints()

const v$ = useValidation(
  () => ({
    collection: {
      required: props.requireBeneficiaries ? requiredValidator : () => true,
      validPercentageDistribution: helpers.withMessage(
        'El porcentaje total asignado debe ser 100%.',
        () => validPercentageDistribution.value
      ),
      minLength: minLengthValidator(
        computed(() => (constraints?.value?.requireBeneficiaries ? 1 : 0))
      ),
    },
  }),
  model,
  { $scope: Form.beneficiaries, $registerAs: Form.beneficiaries }
)

const percentageTotal = computed(() =>
  model.value.collection.reduce((accumulator, item) => accumulator + (item.percentage ?? 0), 0)
)

const validPercentageDistribution = computed(
  () => model.value.collection.length === 0 || percentageTotal.value === 100
)

const distributePercentages = () => {
  const value = 100 / model.value.collection.length
  model.value.collection.forEach((item) => (item.percentage = value))
}

const { addOrReplaceCurrent, clearCurrent, current, removeCurrent, setCurrentAt } =
  useArrayListHandler(model.value.collection, Beneficiary.load(), {
    unshift: true,
  })

const { visible: dialog, show, hide } = useVisible()

const addOrEdit = useDebounceFn(async () => {
  const isValid = await v$.value.beneficiary.$validate()
  if (!isValid) return

  addOrReplaceCurrent()
  hide()
}, 100)

const onEdit = async (index: number) => {
  setCurrentAt(index)
  show()
}

const onRemove = (index: number) => {
  setCurrentAt(index)
  removeCurrent()
}

const onHide = () => {
  clearCurrent()
}
</script>

<template>
  <FormsGlobalContainer class="flex flex-col gap-4">
    <FormsGlobalTitle
      text="Beneficiarios"
      icon-class="icon-[ph--users-three-bold]"
    />

    <div
      v-if="!beneficiariesCountIsMaxed(model.collection)"
      class="text-right col-span-full"
    >
      <PvButton
        class="w-full md:w-auto"
        @click="show"
      >
        <div class="flex items-center gap-2">
          <span class="icon-[ph--plus-circle-bold] text-xl" />
          <span class="uppercase">Agregar beneficiario</span>
        </div>
      </PvButton>
    </div>
    <template v-if="model.collection.length">
      <template
        v-for="(_, i) in model.collection"
        :key="i"
      >
        <FormsBeneficiaryResume
          v-if="model.collection[i]"
          v-model="model.collection[i]"
        >
          <template #actions>
            <PvButton @click="onEdit(i)">
              <span class="icon-[ph--magnifying-glass-bold] text-xl" />
            </PvButton>
            <PvButton
              severity="warn"
              @click="onRemove(i)"
            >
              <span class="icon-[ph--trash-bold] text-xl" />
            </PvButton>
          </template>
        </FormsBeneficiaryResume>
      </template>
    </template>
    <NoteComponent
      v-if="!requireBeneficiaries && !model.collection.length"
      message="En caso de no agregar beneficiarios se declara a herederos legales."
      class="my-4 col-span-full"
    />
    <CardComponent
      v-else
      class="col-span-full"
    >
      <div class="flex flex-col md:flex-row items-center justify-between">
        <div class="text-lg">
          Porcentaje total:
          <span class="font-bold">{{ percentageTotal }}</span>
        </div>
        <PvButton
          v-if="model.collection.length"
          @click="distributePercentages"
        >
          <span class="w-full md:w-auto text-xs">Asignar porcentajes automáticamente</span>
        </PvButton>
      </div>
    </CardComponent>
    <div
      v-if="v$.collection.$error"
      class="p-error p-invalid text-xs"
    >
      * {{ v$.collection?.$errors[0]?.$message.toString() }}
    </div>
  </FormsGlobalContainer>

  <PvDialog
    v-model:visible="dialog"
    class="max-w-[90%] md:w-[75rem]"
    :closable="false"
    @hide="onHide"
  >
    <div class="p-6">
      <FormsBeneficiaryForm
        v-if="current.item"
        v-model="current.item"
      />
    </div>
    <template #footer>
      <div class="w-full">
        <div class="flex justify-end gap-4 py-2">
          <PvButton
            severity="contrast"
            @click="hide"
          >
            <span class="icon-[ph--x-circle-bold]" />
            Descartar
          </PvButton>
          <PvButton @click="addOrEdit">
            <span class="icon-[ph--check-circle-bold]" />
            Agregar
          </PvButton>
        </div>
      </div>
    </template>
  </PvDialog>
</template>
